import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  FaNewspaper, FaArrowLeft, FaClock, FaTrophy, FaSpinner, FaCheck, 
  FaGlobe, FaBrain, FaTwitter, FaLinkedin, FaShare, FaFutbol, 
  FaChartLine, FaTheaterMasks, FaFlag, FaUsers, FaRobot
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { QuizFeedbackBubble } from '../components/CharacterBubble';
import ReportErrorButton from '../components/ReportErrorButton';

// Icônes par catégorie
const CATEGORY_ICONS = {
  general: FaNewspaper,
  sport: FaFutbol,
  international: FaGlobe,
  economie: FaChartLine,
  culture: FaTheaterMasks
};

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Composant de sélection de catégorie et pays
function QuizSelector({ onSelectQuiz, availableQuizzes }) {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [selectedCountry, setSelectedCountry] = useState('FR');
  const [loading, setLoading] = useState(false);

  const categories = [
    { id: 'general', name: 'Actualité générale', icon: FaNewspaper, color: '#1B4965' },
    { id: 'sport', name: 'Sport', icon: FaFutbol, color: '#10B981' },
    { id: 'international', name: 'International', icon: FaGlobe, color: '#6366F1' },
    { id: 'economie', name: 'Économie', icon: FaChartLine, color: '#F59E0B' },
    { id: 'culture', name: 'Culture', icon: FaTheaterMasks, color: '#EC4899' }
  ];

  const countries = [
    { id: 'FR', name: 'France', flag: '🇫🇷' },
    { id: 'BE', name: 'Belgique', flag: '🇧🇪' },
    { id: 'CH', name: 'Suisse', flag: '🇨🇭' },
    { id: 'CA', name: 'Canada', flag: '🇨🇦' }
  ];

  const handleStart = () => {
    setLoading(true);
    onSelectQuiz(selectedCategory, selectedCountry);
  };

  // Vérifier si ce quiz existe déjà
  const quizStatus = availableQuizzes?.[selectedCategory]?.countries?.[selectedCountry];
  const isGenerated = quizStatus?.generated;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="comic-panel mb-6"
      >
        <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>
          <FaNewspaper className="inline mr-2" /> Choisis ton quiz du jour
        </h2>
        
        <p className="text-gray-600 mb-6">
          Tests de connaissance d'actualité et personnalités, renouvelés 7/7, localisés par pays.
        </p>

        {/* Sélection catégorie */}
        <div className="mb-6">
          <h3 className="font-bold mb-3 flex items-center">
            <FaBrain className="mr-2" /> Catégorie
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {categories.map(cat => {
              const Icon = cat.icon;
              const isSelected = selectedCategory === cat.id;
              return (
                <motion.button
                  key={cat.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`p-4 rounded-xl border-3 transition-all ${
                    isSelected 
                      ? 'border-black shadow-[4px_4px_0px_rgba(0,0,0,0.3)]' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ 
                    background: isSelected ? cat.color : 'white',
                    color: isSelected ? 'white' : cat.color
                  }}
                  data-testid={`category-${cat.id}`}
                >
                  <Icon className="text-2xl mx-auto mb-2" />
                  <p className="text-xs font-bold">{cat.name}</p>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Sélection pays */}
        <div className="mb-6">
          <h3 className="font-bold mb-3 flex items-center">
            <FaFlag className="mr-2" /> Pays
          </h3>
          <div className="flex flex-wrap gap-3">
            {countries.map(country => {
              const isSelected = selectedCountry === country.id;
              return (
                <motion.button
                  key={country.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCountry(country.id)}
                  className={`px-4 py-3 rounded-xl border-3 transition-all flex items-center gap-2 ${
                    isSelected 
                      ? 'border-black bg-blue-500 text-white shadow-[4px_4px_0px_rgba(0,0,0,0.3)]' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  data-testid={`country-${country.id}`}
                >
                  <span className="text-2xl">{country.flag}</span>
                  <span className="font-bold">{country.name}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Statut du quiz */}
        <div className="bg-gray-50 p-4 rounded-xl mb-6">
          {isGenerated ? (
            <div className="flex items-center text-green-600">
              <FaCheck className="mr-2" />
              <span>Quiz déjà disponible ({quizStatus.questionCount} questions)</span>
              <span className="ml-2 text-xs text-gray-500">
                Généré par un autre élève ce matin
              </span>
            </div>
          ) : (
            <div className="flex items-center text-orange-600">
              <FaUsers className="mr-2" />
              <span>Tu seras le premier à générer ce quiz aujourd'hui !</span>
            </div>
          )}
        </div>

        {/* Bouton start */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStart}
          disabled={loading}
          className="w-full comic-button text-xl py-4"
          style={{ 
            background: loading ? '#ccc' : 'linear-gradient(135deg, #E63946 0%, #D62839 100%)',
            color: 'white'
          }}
          data-testid="start-quiz-btn"
        >
          {loading ? (
            <><FaSpinner className="inline animate-spin mr-2" /> Chargement...</>
          ) : (
            <><FaNewspaper className="inline mr-2" /> COMMENCER LE QUIZ</>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
}

// Composant Badge partageable
function ShareableBadge({ score, total, onClose, category, country }) {
  const percentage = Math.round((score / total) * 100);
  const categoryNames = {
    general: 'Actualité',
    sport: 'Sport',
    international: 'International',
    economie: 'Économie',
    culture: 'Culture'
  };
  
  const shareOnTwitter = () => {
    const text = `J'ai obtenu ${score}/${total} (${percentage}%) au Quiz ${categoryNames[category] || 'Actu'} sur @DailyGlobeSchool ! #Journalisme #Actualité`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        className="bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-1 rounded-2xl max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl p-6 text-center">
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-6xl mb-4"
          >
            <FaTrophy className="mx-auto text-yellow-500" />
          </motion.div>
          
          <h2 className="comic-title text-2xl text-gray-800 mb-2">
            Quiz Actu Terminé !
          </h2>
          
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg mb-4">
            <p className="text-3xl font-bold">{score}/{total}</p>
            <p className="text-sm opacity-90">{percentage}% de bonnes réponses</p>
          </div>
          
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className={`text-2xl ${i < Math.ceil(score * 5 / total) ? '' : 'grayscale opacity-30'}`}
              >
                ⭐
              </span>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            DailyGlobe School - {new Date().toLocaleDateString('fr-FR')}
          </p>
          
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-3">Partager mon score :</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={shareOnTwitter}
                className="flex items-center gap-2 bg-blue-400 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaTwitter /> Twitter
              </button>
              <button
                onClick={shareOnLinkedIn}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FaLinkedin /> LinkedIn
              </button>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="mt-4 text-gray-500 hover:text-gray-700 text-sm"
          >
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Composant de chargement verbose
function VerboseLoader({ step, steps }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="comic-panel max-w-lg mx-4 text-center"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="text-6xl mb-6 inline-block"
        >
          <FaRobot className="text-blue-500" />
        </motion.div>
        
        <h2 className="comic-title text-2xl mb-6" style={{ color: '#1B4965' }}>
          Génération du Quiz en cours...
        </h2>
        
        <div className="space-y-3 text-left mb-6">
          {steps.map((s, idx) => (
            <motion.div
              key={idx}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.3 }}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                idx < step ? 'bg-green-100' : idx === step ? 'bg-blue-100' : 'bg-gray-100'
              }`}
            >
              <span className="text-2xl">
                {idx < step ? (
                  <FaCheck className="text-green-500" />
                ) : idx === step ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <FaSpinner className="text-blue-500" />
                  </motion.span>
                ) : (
                  <span className="text-gray-400">{s.icon}</span>
                )}
              </span>
              <div className="flex-1">
                <p className={`comic-text ${idx <= step ? 'text-gray-800' : 'text-gray-400'}`}>
                  {s.label}
                </p>
                {idx === step && s.detail && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-sm text-blue-600"
                  >
                    {s.detail}
                  </motion.p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
            initial={{ width: 0 }}
            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        
        <p className="text-sm text-gray-500 mt-4">
          Patience, les meilleures questions arrivent... ☕
        </p>
      </motion.div>
    </div>
  );
}

function DailyQuizPage() {
  const [quiz, setQuiz] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [showBadge, setShowBadge] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [selectedCountry, setSelectedCountry] = useState('FR');
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  
  // Récupérer les paramètres depuis la navigation (si provenant de QuizSelectionPage)
  const navState = location.state;

  const loadingSteps = [
    { icon: <FaGlobe />, label: "Collecte des actualités du jour", detail: "Le Monde, Le Figaro, Libération..." },
    { icon: <FaBrain />, label: "Analyse des articles par l'IA", detail: "Extraction des faits importants" },
    { icon: <FaRobot />, label: "Génération des 30 questions", detail: "Avec 4 choix de réponses chacune" },
    { icon: <FaCheck />, label: "Vérification et validation", detail: "Contrôle qualité des questions" }
  ];

  useEffect(() => {
    // Si on arrive avec un quiz déjà chargé via navigation
    if (navState?.quiz) {
      processQuizData(navState.quiz);
      if (navState.category) setSelectedCategory(navState.category);
      if (navState.country) setSelectedCountry(navState.country);
    } else {
      initQuiz();
    }
  }, []);
  
  const processQuizData = async (quizData) => {
    try {
      // Mélanger les options de chaque question
      const questionsWithShuffledOptions = quizData.questions.map(q => {
        const optionsWithIndex = q.options.map((opt, idx) => ({ text: opt, originalIndex: idx }));
        const shuffled = shuffleArray(optionsWithIndex);
        const newCorrectIndex = shuffled.findIndex(opt => opt.originalIndex === q.correctIndex);
        
        return {
          ...q,
          options: shuffled.map(opt => opt.text),
          correctIndex: newCorrectIndex,
          originalCorrectIndex: q.correctIndex
        };
      });
      
      setQuiz(quizData);
      setShuffledQuestions(questionsWithShuffledOptions);
      
      const startResponse = await api.post('/quiz/start');
      setAttemptId(startResponse.data.attemptId);
    } catch (error) {
      console.error('Error processing quiz:', error);
      setError(error.response?.data?.detail || error.message || "Erreur lors du traitement du quiz");
    } finally {
      setLoading(false);
    }
  };

  // Simuler la progression pendant le chargement
  useEffect(() => {
    if (loading && !quiz) {
      const interval = setInterval(() => {
        setLoadingStep(prev => {
          if (prev < loadingSteps.length - 1) return prev + 1;
          return prev;
        });
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [loading, quiz]);

  const initQuiz = async () => {
    try {
      setError(null);
      setLoadingStep(0);
      
      const quizResponse = await api.get('/quiz/daily');
      
      if (!quizResponse.data || !quizResponse.data.questions || quizResponse.data.questions.length === 0) {
        throw new Error("Quiz invalide ou vide");
      }
      
      // Mélanger les options de chaque question
      const questionsWithShuffledOptions = quizResponse.data.questions.map(q => {
        const optionsWithIndex = q.options.map((opt, idx) => ({ text: opt, originalIndex: idx }));
        const shuffled = shuffleArray(optionsWithIndex);
        const newCorrectIndex = shuffled.findIndex(opt => opt.originalIndex === q.correctIndex);
        
        return {
          ...q,
          options: shuffled.map(opt => opt.text),
          correctIndex: newCorrectIndex,
          originalCorrectIndex: q.correctIndex
        };
      });
      
      setQuiz(quizResponse.data);
      setShuffledQuestions(questionsWithShuffledOptions);
      
      const startResponse = await api.post('/quiz/start');
      setAttemptId(startResponse.data.attemptId);
    } catch (error) {
      console.error('Error initializing quiz:', error);
      setError(error.response?.data?.detail || error.message || "Erreur lors du chargement du quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answerIndex) => {
    if (selectedAnswer !== null || !shuffledQuestions[currentQuestion]) return;

    setSelectedAnswer(answerIndex);
    const question = shuffledQuestions[currentQuestion];
    const isCorrect = answerIndex === question.correctIndex;

    try {
      const response = await api.post('/quiz/answer', {
        questionIndex: currentQuestion,
        answer: String(question.originalCorrectIndex === question.correctIndex ? answerIndex : 
          answerIndex === question.correctIndex ? question.originalCorrectIndex : answerIndex)
      });

      setFeedback(response.data.feedback);
      if (isCorrect) {
        setScore(prev => prev + 1);
      }

      setTimeout(() => {
        if (currentQuestion + 1 >= shuffledQuestions.length) {
          setCompleted(true);
        } else {
          setCurrentQuestion(prev => prev + 1);
          setSelectedAnswer(null);
          setFeedback(null);
        }
      }, 3000);
    } catch (error) {
      console.error('Error submitting answer:', error);
      // Continue anyway to not block the quiz
      setTimeout(() => {
        if (currentQuestion + 1 >= shuffledQuestions.length) {
          setCompleted(true);
        } else {
          setCurrentQuestion(prev => prev + 1);
          setSelectedAnswer(null);
          setFeedback(null);
        }
      }, 3000);
    }
  };

  // Loading state with verbose mode
  if (loading) {
    return <VerboseLoader step={loadingStep} steps={loadingSteps} />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="comic-panel max-w-lg mx-4 text-center"
        >
          <div className="text-6xl mb-4">😕</div>
          <h2 className="comic-title text-2xl mb-4" style={{ color: '#E63946' }}>
            Oups !
          </h2>
          <p className="comic-text text-lg mb-6">{error}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setLoading(true);
                setError(null);
                initQuiz();
              }}
              className="comic-button comic-button-navy"
            >
              Réessayer
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="comic-button bg-gray-200"
            >
              Retour
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // No quiz data state (failsafe)
  if (!quiz || !shuffledQuestions || shuffledQuestions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="comic-panel max-w-lg mx-4 text-center"
        >
          <div className="text-6xl mb-4">📰</div>
          <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>
            Quiz non disponible
          </h2>
          <p className="comic-text text-lg mb-6">
            Le quiz du jour n'est pas encore prêt. Réessayez dans quelques minutes.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setLoading(true);
                initQuiz();
              }}
              className="comic-button comic-button-navy"
            >
              Réessayer
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="comic-button bg-gray-200"
            >
              Retour au Dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Completed state
  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <AnimatePresence>
          {showBadge && (
            <ShareableBadge 
              score={score} 
              total={shuffledQuestions.length} 
              onClose={() => setShowBadge(false)} 
            />
          )}
        </AnimatePresence>
        
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="comic-panel max-w-2xl mx-4 text-center"
        >
          <div className="onomatopoeia mb-6">BRAVO!</div>
          <FaTrophy className="text-8xl mx-auto mb-6" style={{ color: '#FFB703' }} />
          <h2 className="comic-title text-4xl mb-4" style={{ color: '#1B4965' }}>
            Quiz Terminé!
          </h2>
          <p className="text-3xl comic-text mb-6">Score: {score}/{shuffledQuestions.length}</p>
          
          <div className="speech-bubble mb-6">
            <p className="comic-text text-xl">
              {score >= 25 ? "EXCELLENT TRAVAIL! Tu es un vrai Super Reporter! 🌟" :
               score >= 20 ? "BIEN JOUÉ! Continue comme ça! 💪" :
               score >= 15 ? "PAS MAL! Mais tu peux faire mieux! 📰" :
               "CONTINUE À T'ENTRAÎNER! Les super reporters ne baissent jamais les bras! 🚀"}
            </p>
          </div>
          
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => setShowBadge(true)}
              className="comic-button bg-yellow-400 text-black"
            >
              <FaShare className="mr-2" /> Partager
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="comic-button comic-button-navy text-xl"
              data-testid="back-to-dashboard-button"
            >
              Retour au Dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const question = shuffledQuestions[currentQuestion];
  const isCorrect = selectedAnswer !== null && selectedAnswer === question.correctIndex;

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Header */}
      <header className="border-b-4 border-black" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 comic-button bg-white text-black hover:bg-gray-100"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline" /> 
            <span className="hidden sm:inline">Retour Dashboard</span>
            <span className="sm:hidden">Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl comic-title text-white">
            <FaNewspaper className="inline mr-2" />
            Quiz Quotidien
          </h1>
          <div className="text-white text-right">
            <p className="comic-text text-lg">Score: {score}/{currentQuestion}</p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Progress */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="comic-text text-lg">Question {currentQuestion + 1}/{shuffledQuestions.length}</span>
            <span className="comic-badge">{Math.round(((currentQuestion + 1) / shuffledQuestions.length) * 100)}%</span>
          </div>
          <div className="w-full h-4 bg-gray-200 border-3 border-black">
            <motion.div
              className="h-full"
              style={{ background: '#FFB703' }}
              initial={{ width: 0 }}
              animate={{ width: `${((currentQuestion + 1) / shuffledQuestions.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="max-w-4xl mx-auto comic-panel"
          >
            <h2 className="comic-text text-2xl mb-6" data-testid="question-text">
              {question.question}
            </h2>

            <div className="space-y-4" data-testid="answer-options">
              {question.options.map((option, index) => {
                let bgColor = 'bg-white';
                let borderColor = 'border-black';
                
                if (selectedAnswer !== null) {
                  if (index === question.correctIndex) {
                    bgColor = 'bg-green-100';
                    borderColor = 'border-green-500';
                  } else if (index === selectedAnswer && !isCorrect) {
                    bgColor = 'bg-red-100';
                    borderColor = 'border-red-500';
                  }
                }

                return (
                  <motion.button
                    key={index}
                    whileHover={selectedAnswer === null ? { scale: 1.02 } : {}}
                    whileTap={selectedAnswer === null ? { scale: 0.98 } : {}}
                    onClick={() => handleAnswer(index)}
                    disabled={selectedAnswer !== null}
                    className={`w-full p-4 border-3 ${borderColor} ${bgColor} text-left transition-all ${
                      selectedAnswer === null ? 'hover:shadow-lg cursor-pointer' : 'cursor-not-allowed'
                    }`}
                    data-testid={`answer-option-${index}`}
                  >
                    <span className="comic-text text-lg">
                      {String.fromCharCode(65 + index)}. {option}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            {/* Bouton signaler une erreur */}
            <div className="flex justify-end mt-4">
              <ReportErrorButton
                quizId={`daily-quiz-${quiz?.id || 'current'}`}
                questionId={`q-${currentQuestion}`}
                questionText={question.question}
                options={question.options}
                onReported={() => {
                  // Passer à la question suivante sans compter le score
                  setTimeout(() => nextQuestion(), 1500);
                }}
              />
            </div>

            {/* Feedback */}
            {feedback && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="mt-6"
              >
                <QuizFeedbackBubble 
                  isCorrect={selectedAnswer === question.correctAnswer}
                  feedback={feedback}
                  context="quiz"
                />

                {question.explanation && (
                  <div className="mt-4 p-4 bg-blue-50 border-3 border-blue-500 rounded">
                    <p className="text-sm"><strong>Explication:</strong> {question.explanation}</p>
                    {question.sourceUrl && (
                      <a
                        href={question.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-sm mt-2 block"
                      >
                        📰 Source: {question.source}
                      </a>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default DailyQuizPage;
